import React, { useEffect, useState } from 'react';
import { Play, Clock, Calendar } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { useTranslation } from '../hooks/useTranslation';
import AdBanner from '../components/AdBanner';
import { usePageSEO } from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Podcast = () => {
  const { t, language } = useTranslation();
  usePageSEO({
    it: {
      title: 'Podcast — Ascolta On Demand | Rete Italia',
      description: 'I podcast di Rete Italia: interviste, intrattenimento, sport e musica italiana. Ascolta quando vuoi, ovunque tu sia.',
    },
    en: {
      title: 'Podcasts — Listen On Demand | Rete Italia',
      description: 'Rete Italia podcasts: interviews, entertainment, sport and Italian music. Listen anytime, anywhere.',
    },
    basePath: '/podcast',
  });
  const [podcasts, setPodcasts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPodcasts = async () => {
      try {
        const response = await axios.get(`${API}/podcasts`);
        setPodcasts(response.data);
      } catch (error) {
        console.error('Error fetching podcasts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPodcasts();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pt-24 pb-16 flex items-center justify-center">
        <p className="text-xl text-gray-600">Caricamento...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pt-24 pb-16">
      <div className="container mx-auto px-4">
        {/* Advertisement Banner */}
        <AdBanner className="mb-8" />

        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-[#009246] via-[#CE2B37] to-[#00247D] bg-clip-text text-transparent">
              {t('podcast.title')}
            </span>
          </h1>
          <p className="text-xl text-gray-700 max-w-2xl mx-auto">
            {t('podcast.subtitle')}
          </p>
        </div>

        {/* Podcast Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {podcasts.map((podcast) => (
            <div
              key={podcast.id}
              className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group"
            >
              {/* Podcast Cover */}
              <div className="relative overflow-hidden">
                <img
                  src={podcast.image}
                  alt={podcast.title}
                  className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                
                {/* Play Button Overlay */}
                <a
                  href={podcast.link || "https://ilglobo.com/podcasts/"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                >
                  <Button
                    size="lg"
                    className="w-16 h-16 rounded-full bg-white/90 hover:bg-white shadow-2xl"
                  >
                    <Play className="w-8 h-8 text-[#009246] fill-[#009246] ml-1" />
                  </Button>
                </a>
              </div>

              {/* Podcast Info */}
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-[#009246] transition-colors">
                  {podcast.title}
                </h3>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  {podcast.description}
                </p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    {/* Count/duration are optional: a new podcast with no episode
                        tally yet shows neither, rather than "undefined episodi". */}
                    {podcast.episodes ? (
                      <div className="flex items-center gap-1 font-semibold text-[#009246]">
                        <span>{podcast.episodes} {t('podcast.episodes')}</span>
                      </div>
                    ) : null}
                    {podcast.duration ? (
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        <span>{podcast.duration}</span>
                      </div>
                    ) : null}
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>{t('podcast.weekly')}</span>
                    </div>
                  </div>
                  <a
                    href={podcast.link || "https://ilglobo.com/podcasts/"}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button
                      className="bg-gradient-to-r from-[#009246] to-[#00b359] hover:from-[#00b359] hover:to-[#009246] transition-all duration-300"
                    >
                      {t('podcast.listen')}
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Newsletter Section */}
        <div className="mt-16 bg-gradient-to-r from-[#CE2B37] to-[#e63946] rounded-3xl p-8 md:p-12 text-white shadow-2xl">
          <h2 className="text-2xl md:text-3xl font-bold mb-4 text-center">{t('podcast.ctaTitle')}</h2>
          <p className="text-lg md:text-xl mb-8 opacity-90 text-center">
            {t('podcast.ctaSubtitle')}
          </p>
          
          {/* Newsletter Form */}
          <form 
            className="js-cm-form max-w-2xl mx-auto" 
            id="subForm" 
            action="https://www.createsend.com/t/subscribeerror?description=" 
            method="post" 
            data-id="2BE4EF332AA2E32596E38B640E90561912CC059D7F15ABC264AB027F536F4A9DB2BE5BCA10F970DF3F1CB4D26833305849D365EB109D5E5D77ABA87C0268BF53"
          >
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="fieldName" className="block text-sm font-medium mb-2">
                    {language === 'it' ? 'Nome' : 'Name'}
                  </label>
                  <input 
                    id="fieldName" 
                    maxLength="200" 
                    name="cm-name" 
                    placeholder={language === 'it' ? 'Il tuo nome' : 'Your name'}
                    className="w-full px-4 py-3 rounded-lg text-gray-900 focus:ring-2 focus:ring-white focus:outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="fieldEmail" className="block text-sm font-medium mb-2">
                    {language === 'it' ? 'Indirizzo email' : 'Email address'}
                  </label>
                  <input 
                    autoComplete="Email" 
                    className="js-cm-email-input qa-input-email w-full px-4 py-3 rounded-lg text-gray-900 focus:ring-2 focus:ring-white focus:outline-none" 
                    id="fieldEmail" 
                    maxLength="200" 
                    name="cm-itbkj-itbkj" 
                    placeholder={language === 'it' ? 'tua@email.com' : 'your@email.com'}
                    required 
                    type="email"
                  />
                </div>
              </div>
              
              <fieldset className="mt-4">
                <legend className="text-sm font-medium mb-3">{language === 'it' ? 'Le mie iscrizioni' : 'My subscriptions'}</legend>
                <div className="flex flex-wrap gap-4 justify-center">
                  <label className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-lg cursor-pointer hover:bg-white/30 transition-colors">
                    <input id="3901480" name="cm-fo-kkkiuu" type="checkbox" value="3901480" className="w-4 h-4" />
                    <span>Rete Italia</span>
                  </label>
                  <label className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-lg cursor-pointer hover:bg-white/30 transition-colors">
                    <input id="1320629" name="cm-fo-kkkiuu" type="checkbox" value="1320629" className="w-4 h-4" />
                    <span>Il Globo</span>
                  </label>
                  <label className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-lg cursor-pointer hover:bg-white/30 transition-colors">
                    <input id="1320630" name="cm-fo-kkkiuu" type="checkbox" value="1320630" className="w-4 h-4" />
                    <span>La Fiamma</span>
                  </label>
                </div>
              </fieldset>
              
              <div className="text-center mt-6">
                <button 
                  type="submit"
                  className="bg-white text-[#CE2B37] px-8 py-4 rounded-xl text-lg font-bold shadow-xl hover:bg-gray-100 hover:scale-105 transition-all duration-300"
                >
                  {t('podcast.subscribeNow')}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Podcast;